import { useEffect, useMemo, useState } from 'react';
import { RoleTaskFull } from 'interfaces/roleTask.interface';
import { Ability, RootState, User } from '@umanforge/common';
import { useSelector, useDispatch } from 'react-redux';
import { GlobalService } from 'services/global.service';
import { SelectOption } from 'interfaces/select_option.interfaces';
import { dayjs, ShortUser } from '@umanforge/common';
import { SkillRT } from 'interfaces/roleTaskSkill.interface';
import { RoleTaskService } from 'services/roleTask.service';


interface RoleTaskError {
  users: string[] | undefined
}

export function useRoleTaskTileForm(
  onChange: ( roleTask: RoleTaskFull, index: number, forceUpdate: boolean ) => void,
  onDelete: ( roleTask: RoleTaskFull, index: number ) => void,
  roleTask: RoleTaskFull,
  sectorUsers: ShortUser[],
  index: number,
  skills: SkillRT[],
  abilities: Ability[],
  fixed: boolean = false
) {
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );
  const dispatch = useDispatch();

  const [ isFirstRender, setIsFirstRender ] = useState( true );
  const [ userOptions, setUserOptions ] = useState<SelectOption<ShortUser>[]>( [] );

  const [ count, setCount ] = useState( 1 );
  const [ errors, setErrors ] = useState<RoleTaskError>( );

  const [ selectedAbilities, setSelectedAbilities ] = useState<SelectOption<Ability>[]>( [] );
  const [ selectedSkills, setSelectedSkills ] = useState<SelectOption<SkillRT>[]>( [] );
  const [ selectedUsers, setSelectedUsers ] = useState<SelectOption<ShortUser>[]>( [] );

  const init = async () => {
    const _selectedAbilities = GlobalService.toSelectOption( roleTask.abilities )
      .sort( ( item1, item2 ) => {
        const date1 = item1.object.expirationDate && item1.object.expirationDate != '' ? dayjs( item1.object.expirationDate ) : dayjs();
        const date2 = item2.object.expirationDate && item2.object.expirationDate != '' ? dayjs( item2.object.expirationDate ) : dayjs();
        if ( date1.isSame( date2, 'date' ) ) {
          return item1.object.name.toUpperCase() < item2.object.name.toUpperCase() ? -1 : 1;
        }
        return date1.isBefore( date2, 'date' ) ? -1 : 1;
      } );

    const _selectedSkills = GlobalService.toSelectOption( roleTask.skills )
      .sort( ( item1, item2 ) => {
        const value1 = item1.object.iluo ?? 1;
        const value2 = item2.object.iluo ?? 1;
        if ( value1 === value2 ) {
          return item1.object.name.toUpperCase() < item2.object.name.toUpperCase() ? -1 : 1;
        }
        return ( value1 < value2 ) ? -1 : 1;
      } );

    const _selectedUsers = GlobalService.toSelectOption( roleTask.users )
      .sort( ( item1, item2 ) => {
        return item1.object.fullname.toUpperCase() < item2.object.fullname.toUpperCase() ? -1 : 1;
      } );

    setSelectedAbilities( _selectedAbilities );
    setSelectedSkills( _selectedSkills );
    setSelectedUsers( _selectedUsers );
    setCount( roleTask.count );

    // Fetch users
    if ( sectorUsers.length <= 0 ) {
      setUserOptions( GlobalService.toSelectOption( [] ) );
    }
    else if ( _selectedSkills.length <= 0 && _selectedAbilities.length <= 0 ) {
      setUserOptions( GlobalService.toSelectOption( sectorUsers ) );
    }
    else {
      const allowedUsers = await RoleTaskService.getUsersByAbilitiesSkillsUsers(
        sectorUsers.map( user => { return user.id; } ),
        _selectedAbilities.map( sa => { return sa.object.id; } ),
        _selectedSkills.map( sk => { return { id: sk.object.id, value: sk.object.iluo }; } ),
        token,
        dispatch
      );
      setUserOptions( GlobalService.toSelectOption( allowedUsers ) );
    }

    setIsFirstRender( false );
  };

  // UseEffects
  useEffect( () => {
    init();
  }, [ ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      fetchUser();
    }
  }, [ selectedAbilities, selectedSkills, count, sectorUsers ] );

  useEffect( () => {
    if ( !isFirstRender && !fixed ) {
      const userError: string[] = [];
      if ( selectedUsers.length > 0 ) {
        selectedUsers.forEach( user => {
          if ( !userOptions.some( opt => { return opt.object.id == user.object.id; } ) ) {
            userError.push( user.object.fullname );
          }
        } );
      }
      if ( userError.length > 0 ) {
        setErrors( { users: [ ...new Set( userError ) ] } );
      }
      else {
        setErrors( undefined );
      }
      handleChanges( userError.length <= 0 );
    }
  }, [ userOptions, selectedUsers ] );


  // Memo
  const { skillOptions, abilityOptions } = useMemo( () => {
    return  {
      skillOptions: GlobalService.toSelectOption( skills.map( sk => { return { ...sk, iluo: 1, value: 1 }; } ) ),
      abilityOptions: GlobalService.toSelectOption( abilities )
    };
  }, [ skills, abilities ] );


  // Fetch users
  const fetchUser = async () => {
    if ( sectorUsers.length <= 0 ) {
      setUserOptions( GlobalService.toSelectOption( [] ) );
    }
    else if ( selectedSkills.length <= 0 && selectedAbilities.length <= 0 ) {
      setUserOptions( GlobalService.toSelectOption( sectorUsers ) );
    }
    else {
      const allowedUsers = await RoleTaskService.getUsersByAbilitiesSkillsUsers(
        sectorUsers.map( user => { return user.id; } ),
        selectedAbilities.map( sa => { return sa.object.id; } ),
        selectedSkills.map( sk => { return { id: sk.object.id, value: sk.object.iluo }; } ),
        token,
        dispatch
      );
      setUserOptions( GlobalService.toSelectOption( allowedUsers ) );
    }
  };


  // Handles
  const handleChanges = ( isValid: boolean = true ) => {
    if ( !isFirstRender ) {
      const newRoleTask = { ...roleTask };

      newRoleTask.abilities = selectedAbilities.map( selAbl => { return selAbl.object as Ability; } );
      newRoleTask.skills = selectedSkills.map( selSkill => { return selSkill.object as SkillRT; } );
      newRoleTask.users = selectedUsers.map( selUser => { return selUser.object as User; } );
      newRoleTask.count = count;
      newRoleTask.isValid = isValid;

      onChange( newRoleTask, index, true );
    }
  };

  return {
    skillOptions,
    abilityOptions,
    userOptions,
    count, setCount,
    selectedAbilities, setSelectedAbilities,
    selectedSkills, setSelectedSkills,
    selectedUsers, setSelectedUsers,
    errors
  };
};
